import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Activity, Shield, Zap, ArrowRight, History, 
  Stethoscope, Pill, ChevronRight, LayoutDashboard, Database
} from "lucide-react";

// Image import
import backImg from "./back.jpg";

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-600">
      
      {/* 1. NAVBAR */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
              <Stethoscope className="text-white" size={20} strokeWidth={2.5} />
            </div>
            <span className="font-[900] text-2xl tracking-tighter text-slate-900 italic uppercase">
              Avaami<span className="text-blue-600">OP</span>
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            <a href="#solutions" className="hover:text-blue-600 transition-colors">Platform</a>
            <a href="#features" className="hover:text-blue-600 transition-colors">Capabilities</a>
            <a href="#security" className="hover:text-blue-600 transition-colors">Data Privacy</a>
          </div>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/login")}
              className="text-[11px] font-black uppercase tracking-widest text-slate-900 px-5 py-2 hover:text-blue-600 transition-all"
            >
              Login
            </button>
            <button 
              onClick={() => navigate("/register")}
              className="hidden sm:block text-[11px] font-black uppercase tracking-widest bg-slate-900 text-white px-7 py-3.5 rounded-2xl hover:bg-blue-600 shadow-xl shadow-slate-200 transition-all active:scale-95"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* 2. HERO SECTION */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        {/* Abstract Background Decoration */}
        <div className="absolute top-0 right-0 -z-10 w-2/3 h-full bg-gradient-to-l from-blue-50/40 to-transparent rounded-full blur-3xl translate-x-1/3 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-10 animate-in fade-in slide-in-from-left-8 duration-1000">
            <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full border border-blue-100">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-700">The Next-Gen Clinical OS</span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-[950] text-slate-900 leading-[0.95] tracking-tight italic uppercase">
              REDEFINING <br />
              <span className="text-blue-600">PATIENT CARE.</span>
            </h1>

            <p className="text-lg text-slate-500 font-bold max-w-md leading-relaxed tracking-tight">
              A high-performance workspace for doctors. Manage consultations, 
              track medical histories, and digitize your clinic in seconds.
            </p>

            <div className="flex flex-col sm:flex-row gap-5 pt-4">
              <button 
                onClick={() => navigate("/register")}
                className="bg-blue-600 text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-3 hover:bg-slate-900 hover:-translate-y-1 transition-all shadow-2xl shadow-blue-200"
              >
                Register Clinic <ArrowRight size={16} strokeWidth={3} />
              </button>
              <button className="bg-white border-2 border-slate-100 text-slate-900 px-10 py-5 rounded-2xl font-black uppercase tracking-widest text-[11px] hover:bg-slate-50 transition-all">
                Product Tour
              </button>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-12 pt-12 border-t border-slate-100">
              <div className="flex flex-col gap-1">
                <p className="text-2xl font-black text-slate-900">0.4s</p>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Record Retrieval</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-2xl font-black text-slate-900">ISO</p>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Health Standards</p>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Dashboard Preview */}
          <div className="relative animate-in fade-in slide-in-from-right-10 duration-1000">
            <div className="absolute -inset-10 bg-blue-500/5 blur-[100px] rounded-full"></div>
            <div className="relative p-3 bg-white border border-slate-100 rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)]">
              <img 
                src={backImg} 
                alt="Medical Dashboard" 
                className="w-full h-auto rounded-[2.5rem] object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-700"
              />
              
              {/* Floating Pro Badge */}
              <div className="absolute -bottom-10 -right-6 bg-slate-900 text-white p-6 rounded-[2rem] shadow-2xl hidden lg:block border-4 border-white animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white">
                    <History size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-blue-400">New Feature</p>
                    <p className="text-sm font-black italic uppercase">Visit Timeline v2.0</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CAPABILITIES / FEATURES */}
      <section id="features" className="py-32 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-[11px] font-black uppercase tracking-[0.5em] text-blue-600 mb-4">The Avaami Tech Stack</h2>
            <h3 className="text-4xl font-[900] text-slate-900 uppercase italic tracking-tighter">Everything you need <br/> to run a modern practice.</h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: <LayoutDashboard size={24} strokeWidth={2.5}/>, 
                title: "Smart Queue", 
                desc: "Real-time waiting list management with instant 'Consult' triggers for doctors." 
              },
              { 
                icon: <Database size={24} strokeWidth={2.5}/>, 
                title: "Timeline Archives", 
                desc: "Every patient visit organized in a beautiful, searchable medical timeline." 
              },
              { 
                icon: <Pill size={24} strokeWidth={2.5}/>, 
                title: "Rx Engine", 
                desc: "Rapid prescription builder with auto-suggestions and print-ready formats." 
              }
            ].map((f, i) => (
              <div key={i} className="group bg-white p-10 rounded-[2.5rem] border border-slate-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500">
                <div className="h-14 w-14 bg-slate-50 rounded-2xl flex items-center justify-center text-blue-600 mb-8 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500">
                  {f.icon}
                </div>
                <h4 className="text-xl font-black text-slate-900 mb-4 uppercase italic tracking-tight">{f.title}</h4>
                <p className="text-sm text-slate-400 font-bold leading-relaxed">{f.desc}</p>
                <div className="mt-8 flex items-center gap-2 text-[10px] font-black text-blue-600 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all">
                  Learn More <ChevronRight size={14}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-20 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="flex items-center gap-3">
             <div className="h-8 w-8 bg-slate-900 rounded-lg flex items-center justify-center text-white">
                <Activity size={16} strokeWidth={3}/>
             </div>
             <span className="font-black text-xl tracking-tighter italic uppercase">Avaami<span className="text-blue-600">OP</span></span>
          </div>
          
          <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.3em]">
            Empowering Doctors. Digitizing Care. © 2026
          </p>

          <div className="flex gap-8 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <a href="#" className="hover:text-blue-600 transition-colors">Privacy</a>
            <a href="#" className="hover:text-blue-600 transition-colors">Terms</a>
            <a href="#" className="hover:text-blue-600 transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;